// ------------------------------------------------------------------------ //
// TanStack Query hooks for Inquiries
// ------------------------------------------------------------------------ //
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@shared/api/api';
import dayjs from 'dayjs';

// ------------------------------------------------------------------------ //
// Query Keys
// ------------------------------------------------------------------------ //
export const inquiryKeys = {
  all: ['inquiries'],
  direct: () => [...inquiryKeys.all, 'direct'],
  processed: (timestampFrom) => [...inquiryKeys.all, 'processed', timestampFrom],
  waitingSwitchman: () => [...inquiryKeys.all, 'waitingSwitchman'],
  withSwitchman: () => [...inquiryKeys.all, 'withSwitchman'],
  waitingTeacher: () => [...inquiryKeys.all, 'waitingTeacher'],
  withTeacher: () => [...inquiryKeys.all, 'withTeacher'],
};

// ------------------------------------------------------------------------ //
// Get Direct Inquiries
// ------------------------------------------------------------------------ //
export function useDirectInquiries() {
  return useQuery({
    queryKey: inquiryKeys.direct(),
    queryFn: async () => {
      const response = await api.getDirectInquiries({
        timestampFrom: dayjs().subtract(2, 'day').unix(),
      });
      return response.data;
    },
    staleTime: 5000, // Consider data fresh for 5 seconds
    refetchInterval: 5000, // Refetch every 5 seconds
  });
}

// ------------------------------------------------------------------------ //
// Get Processed Inquiries
// ------------------------------------------------------------------------ //
export function useProcessedInquiries(timestampFrom) {
  return useQuery({
    queryKey: inquiryKeys.processed(timestampFrom),
    queryFn: async () => {
      const response = await api.getProcessedInquiries({
        timestampFrom: timestampFrom,
      });

      // Filter out inquiries with status: Échec, Abandon, or Annulation du RDV
      // Only keep inquiries with status "Succès" (or without a status object as fallback)
      const filteredData = response.data.filter((inquiry) => {
        if (!inquiry.status || !inquiry.status.name) {
          // If no status info, keep it (fallback for backward compatibility)
          return true;
        }
        // Exclude: Échec, Abandon, Annulation du RDV
        const excludedStatuses = ['Échec', 'Echec', 'Abandon', 'Annulation du RDV'];
        return !excludedStatuses.includes(inquiry.status.name);
      });

      return filteredData;
    },
    enabled: !!timestampFrom,
    staleTime: 60000,
  });
}

// ------------------------------------------------------------------------ //
// Get Waiting Switchman Inquiries
// ------------------------------------------------------------------------ //
export function useWaitingSwitchmanInquiries() {
  return useQuery({
    queryKey: inquiryKeys.waitingSwitchman(),
    queryFn: async () => {
      return api.getInquiries({
        statusId: 4,
        timestampFrom: dayjs().subtract(2, 'day').unix(),
        hasSwitchman: 0,
        hasTeacher: 0,
      });
    },
    staleTime: 30000,
  });
}

// ------------------------------------------------------------------------ //
// Get With Switchman Inquiries
// ------------------------------------------------------------------------ //
export function useWithSwitchmanInquiries() {
  return useQuery({
    queryKey: inquiryKeys.withSwitchman(),
    queryFn: async () => {
      return api.getInquiries({
        statusId: 4,
        timestampFrom: dayjs().subtract(2, 'day').unix(),
        hasTeacher: 0,
      });
    },
    staleTime: 30000,
  });
}

// ------------------------------------------------------------------------ //
// Get Waiting Teacher Inquiries
// ------------------------------------------------------------------------ //
export function useWaitingTeacherInquiries() {
  return useQuery({
    queryKey: inquiryKeys.waitingTeacher(),
    queryFn: async () => {
      return api.getInquiries({
        statusId: 7,
        timestampFrom: dayjs().subtract(2, 'day').unix(),
        hasTeacher: 0,
      });
    },
    staleTime: 30000,
  });
}

// ------------------------------------------------------------------------ //
// Get With Teacher Inquiries
// ------------------------------------------------------------------------ //
export function useWithTeacherInquiries() {
  return useQuery({
    queryKey: inquiryKeys.withTeacher(),
    queryFn: async () => {
      return api.getInquiries({
        statusId: 7,
        timestampFrom: dayjs().subtract(2, 'day').unix(),
      });
    },
    staleTime: 30000,
  });
}
